import React, { useMemo } from 'react';
import { View, Text, StyleSheet, ScrollView } from 'react-native';
import { useTheme } from '../styles/ThemeProvider';
import { useCommonStyles } from '../styles/useCommonStyles';
import { Achievement } from '../utils/milestones';

interface AchievementsBadgesProps {
  achievements: Achievement[];
}

export default function AchievementsBadges({ achievements }: AchievementsBadgesProps) {
  const theme = useTheme();
  const commonStyles = useCommonStyles();
  const styles = useMemo(
    () =>
      StyleSheet.create({
        container: {
          backgroundColor: theme.colors.backgroundSurface,
          borderWidth: theme.borders.borderWidth,
          borderColor: theme.borders.borderColor,
          borderRadius: theme.radius.radiusMd,
          padding: theme.spacing.lg,
          marginBottom: theme.spacing.xl,
        },
        header: {
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: theme.spacing.lg,
        },
        title: {
          ...theme.typography.h4,
          color: theme.colors.textPrimary,
        },
        progress: {
          ...theme.typography.sm,
          color: theme.colors.textSecondary,
        },
        categoryTitle: {
          marginBottom: theme.spacing.sm,
          marginTop: theme.spacing.sm,
        },
        badgeRow: {
          marginHorizontal: -theme.spacing.lg,
          marginBottom: theme.spacing.sm,
        },
        badgeRowContent: {
          paddingHorizontal: theme.spacing.lg,
          gap: theme.spacing.md,
        },
        badge: {
          width: 100,
          padding: theme.spacing.md,
          alignItems: 'center',
          borderWidth: theme.borders.borderWidth,
          borderRadius: theme.radius.radiusMd,
        },
        badgeUnlocked: {
          borderColor: theme.colors.accent,
          backgroundColor: theme.colors.backgroundElevated,
        },
        badgeLocked: {
          borderColor: theme.borders.borderColor,
          backgroundColor: theme.colors.backgroundPrimary,
        },
        badgeEmoji: {
          fontSize: 28,
          marginBottom: theme.spacing.sm,
        },
        badgeTitle: {
          ...theme.typography.sm,
          fontFamily: theme.fontFamily.bodyMedium,
          color: theme.colors.textPrimary,
          textAlign: 'center',
        },
        badgeTitleLocked: {
          color: theme.colors.textTertiary,
        },
        badgeDescription: {
          ...theme.typography.xs,
          color: theme.colors.textSecondary,
          textAlign: 'center',
          marginTop: theme.spacing.xs,
          letterSpacing: 0,
        },
      }),
    [theme],
  );

  const unlockedCount = achievements.filter(a => a.unlocked).length;
  const totalCount = achievements.length;

  const itemAchievements = achievements.filter(a => a.type === 'items');
  const valueAchievements = achievements.filter(a => a.type === 'value');

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.title}>Achievements</Text>
        <Text style={styles.progress}>
          {unlockedCount}/{totalCount} unlocked
        </Text>
      </View>

      <Text style={[commonStyles.sectionLabel, styles.categoryTitle]}>Collection Size</Text>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        style={styles.badgeRow}
        contentContainerStyle={styles.badgeRowContent}
      >
        {itemAchievements.map((achievement) => (
          <View
            key={achievement.key}
            style={[
              styles.badge,
              achievement.unlocked ? styles.badgeUnlocked : styles.badgeLocked,
            ]}
          >
            <Text style={styles.badgeEmoji}>
              {achievement.unlocked ? achievement.emoji : '🔒'}
            </Text>
            <Text
              style={[
                styles.badgeTitle,
                !achievement.unlocked && styles.badgeTitleLocked,
              ]}
            >
              {achievement.title}
            </Text>
            {achievement.unlocked && (
              <Text style={styles.badgeDescription} numberOfLines={2}>
                {achievement.description}
              </Text>
            )}
          </View>
        ))}
      </ScrollView>

      <Text style={[commonStyles.sectionLabel, styles.categoryTitle]}>Portfolio Value</Text>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        style={styles.badgeRow}
        contentContainerStyle={styles.badgeRowContent}
      >
        {valueAchievements.map((achievement) => (
          <View
            key={achievement.key}
            style={[
              styles.badge,
              achievement.unlocked ? styles.badgeUnlocked : styles.badgeLocked,
            ]}
          >
            <Text style={styles.badgeEmoji}>
              {achievement.unlocked ? achievement.emoji : '🔒'}
            </Text>
            <Text
              style={[
                styles.badgeTitle,
                !achievement.unlocked && styles.badgeTitleLocked,
              ]}
            >
              {achievement.title}
            </Text>
            {achievement.unlocked && (
              <Text style={styles.badgeDescription} numberOfLines={2}>
                {achievement.description}
              </Text>
            )}
          </View>
        ))}
      </ScrollView>
    </View>
  );
}
